import { SelectChangeEvent } from "@mui/material";
import { sectionType } from "../../../sectionType";
import { alignment } from "../models/alignment.enum";
import { Column } from "../models/column";
import { ColumnDefinition, FloorDefinition, SegmentDefinition, VariantDefinition } from "../models/ColumnDefinition/column-definition";
import { Part } from "../models/part";

type ColumnDefinitionAny = ColumnDefinition<any, any, any, any, any>;
type SegmentDefinitionAny = SegmentDefinition<any, any, any>;
type FloorDefinitionAny = FloorDefinition<any>;
type VariantDefinitionAny = VariantDefinition<any>;

export class RefineService {

    static getSegmentTypes<ColumnType, SegmentType>(ct: ColumnType, columnDefs: ColumnDefinitionAny[]): SegmentType[] {
        const coldef = columnDefs.find(c => c.type === ct)
        if (coldef) {
            const types = coldef.segmentDefinitions.map(s => s.type);
            const setOfTypes = new Set(types);
            return Array.from(setOfTypes);
        } else {
            return [];
        }
    }

    static getSegmentDefinitionsForType<ColumnType, SegmentType>(ct: ColumnType, segment: SegmentType, columnDefs: ColumnDefinitionAny[]): SegmentDefinitionAny[] {
        const coldef = columnDefs.find(c => c.type === ct)
        if (coldef) {
            const segments = coldef.segmentDefinitions.filter(s => s.type === segment);
            return segments;
        } else {
            return [];
        }
    }


    static getSegmentTypeSubtypes<ColumnType, SegmentType, SubtypeEnum>(ct: ColumnType, segment: SegmentType, columnDefs: ColumnDefinitionAny[]): SubtypeEnum[] {
        const coldef = columnDefs.find(c => c.type === ct)
        if (coldef) {
            const segments = coldef.segmentDefinitions.filter(s => s.type === segment);
            return segments.map(s => s.subtype);
        } else {
            return [];
        }
    }

    static getSegmentDefinitionFromTypeAndSubtype<ColumnType, SegmentType, SubtypeEnum>(ct: ColumnType, segment: SegmentType, subtypeEnum: SubtypeEnum, columnDefs: ColumnDefinitionAny[]): SegmentDefinitionAny {
        const coldef = columnDefs.find(c => c.type === ct)
        if (coldef) {
            const segments = coldef.segmentDefinitions.filter(s => s.type === segment);
            const seg = segments.find(s => s.subtype === subtypeEnum);
            return seg;
        }
    }

    static getSegmentFloorDefinitionsForSubtype<ColumnType, SegmentType, SubtypeEnum>(ct: ColumnType, subtype: SubtypeEnum, segment: SegmentType, columnDefs: ColumnDefinitionAny[]): FloorDefinitionAny[] {
        const seg = RefineService.getSegmentDefinitionFromTypeAndSubtype(ct, segment, subtype, columnDefs);
        return seg?.floorVariants;
    }

    static getSegmentFloorVariants<ColumnType, SegmentType, SubtypeEnum>(ct: ColumnType, subtype: SubtypeEnum, segment: SegmentType, floors: number, columnDefs: ColumnDefinitionAny[]): VariantDefinitionAny[] {
        const seg = RefineService.getSegmentDefinitionFromTypeAndSubtype(ct, segment, subtype, columnDefs);
        return seg?.floorVariants.filter(s => s.floors === floors).map(fv => fv.variants).flat();
    }

    static handleColumnSegmentCustomChange<SegmentType>(
        part: Part<any, any>,
        column: Column<any, any, any, any>,
        st: SegmentType,
        columnDefs: ColumnDefinitionAny[],
        defaultHeight: number,
        updateBuildingColumn,
    ) {
        const col = { ...column, segments: [...column.segments] }
        const segment = column.segments.find(s => s.type === st);

        const segmentDefs = RefineService.getSegmentDefinitionsForType(column.type, st, columnDefs);
        const segmentDef = segmentDefs[0];
        segment.custom = !segment.custom;
        if (segment.custom) {
            segment.customSegmentHeight = defaultHeight * 2;
            segment.customSegmentWidth = 10;
            segment.sectionTypes = [sectionType.none]
        } else {
            segment.customSegmentHeight = 0;
            segment.customSegmentWidth = 0;
            segment.sectionTypes = segmentDef.floorVariants[0].variants[0].sections;
            segment.subtype = segmentDef.subtype;
            segment.floorsInSegment = segmentDef.floorVariants[0].floors;
            segment.variant = segmentDef.floorVariants[0].variants[0].variant;
            if (part.type === 'headRight' && segmentDef.floorVariants[0].variants.length === 2) {
                segment.variant = segmentDef.floorVariants[0].variants[1].variant;
            }
        }
        col.segments = col.segments.map(s => s === segment ? segment : s);
        updateBuildingColumn(part, { ...col, segments: [...col.segments.map(s => ({ ...s }))] });
    }


    static handleColumnSegmentWidthAndHeight<SegmentType>(
        part: Part<any, any>,
        column: Column<any, any, any, any>,
        st: SegmentType,
        width: number,
        height: number,
        updateBuildingColumn,
    ) {
        const col = { ...column, segments: [...column.segments] }
        const segment = column.segments.find(s => s.type === st);
        segment.customSegmentHeight = height;
        segment.customSegmentWidth = width;
        col.segments = col.segments.map(s => s === segment ? segment : s);
        updateBuildingColumn(part, { ...col, segments: [...col.segments.map(s => ({ ...s }))] });
    }

    static handleColumnSegmentFloorAndVariantChange<SegmentType, VariantEnum>(
        e,
        part: Part<any, any>,
        column: Column<any, any, any, any>,
        st: SegmentType,
        variantChange: boolean,
        repeatingFloorTypes: SegmentType[],
        updateBuildingColumn,
        columnDefs: ColumnDefinitionAny[]
    ) {
        const col = { ...column, segments: [...column.segments] }

        let floorNr: number;
        let variant: VariantEnum;

        const segment = column.segments.find(s => s.type === st);

        if (variantChange) {
            floorNr = +segment.floorsInSegment;
            variant = e.target.value;
        } else {
            floorNr = +e.target.value;
        }

        const segmentDef = RefineService.getSegmentDefinitionFromTypeAndSubtype(column.type, st, segment.subtype, columnDefs);

        const floorsForDef = repeatingFloorTypes.includes(st) ? 1 : floorNr;
        const floorDef = segmentDef.floorVariants.find(fv => fv.floors === floorsForDef);
        if (!variantChange) {
            variant = segment.variant ? segment.variant : ((part.type === 'headRight' && floorDef.variants.length === 2) ? floorDef.variants[1].variant : floorDef.variants[0].variant);
        }

        segment.floorsInSegment = floorNr;
        segment.variant = variant;
        segment.sectionTypes = floorDef.variants.find(fv => fv.variant === variant).sections;
        col.segments = col.segments.map(s => s === segment ? segment : s);

        updateBuildingColumn(part, { ...col, segments: [...col.segments.map(s => ({ ...s }))] });
    }

    static deleteColumn(event: Event | any, part: Part<any, any>, column: Column<any, any, any, any>, updateBuildingColumns) {
        event.preventDefault();
        event.stopPropagation();

        part.columns = part.columns.filter(c => c !== column);

        updateBuildingColumns(part, part.columns.map(c => ({ ...c })));
    }

    static addColumn(event: Event | any, part, column, align: alignment, defaultColumnTypeForPart: string, updateBuildingColumns, setUpDefaultColumnFromDefinition): string {
        event.preventDefault();
        event.stopPropagation();
        let newColumn;
        let columns = [];
        if (part.columns.length > 0 && column) {
            part.columns.forEach(c => {
                if (align === alignment.left && c === column) {
                    newColumn = { ...c, id: Math.random().toString(), segments: [...c.segments.map(s => ({ ...s }))] }
                    columns.push(newColumn);
                }
                columns.push(c);
                if (align === alignment.right && c === column) {
                    newColumn = { ...c, id: Math.random().toString(), segments: [...c.segments.map(s => ({ ...s }))] }
                    columns.push(newColumn);
                }
            })
        } else if (part.columns.length > 0 && !column) {
            columns = part.columns;
            newColumn = setUpDefaultColumnFromDefinition(defaultColumnTypeForPart, part.type);
            columns.push(newColumn);
        } else {
            newColumn = setUpDefaultColumnFromDefinition(defaultColumnTypeForPart, part.type);
            columns.push(newColumn);
        }
        updateBuildingColumns(part, columns.map(c => ({ ...c })));

        return newColumn.id;
    }

    static moveColumnLeft(columnId: string, part: Part<any, any>, placeOnTop: boolean, updateBuildingColumns, updateOptions) {
        const columnIndex = part.columns.findIndex(s => s.id === columnId);
        if (columnIndex > 0) {
            this.handleColumnReorder(columnIndex, columnIndex - 1, part, true, updateBuildingColumns);
        }
    }

    static moveColumnRight(columnId: string, part: Part<any, any>, placeOnTop: boolean, updateBuildingColumns, updateOptions) {
        const columnIndex = part.columns.findIndex(s => s.id === columnId);
        if (columnIndex < part.columns.length - 1) {
            this.handleColumnReorder(columnIndex, columnIndex + 1, part, false, updateBuildingColumns);
        }
    }

    static handleColumnReorder(columnFrom: number, columnTo: number, part: Part<any, any>, placeOnTop: boolean, updateBuildingColumns) {
        if (columnFrom !== columnTo) {
            const clonedColumns = [...part.columns.map(c => ({ ...c }))];
            const colFrom = { ...part.columns[+columnFrom] };
            part.columns[+columnFrom] = { ...part.columns[+columnTo], order: columnFrom };
            part.columns[+columnTo] = colFrom;

            if (placeOnTop) {
                clonedColumns.splice(columnTo, 0, colFrom);
                if (columnTo < columnFrom) {
                    clonedColumns.splice(columnFrom + 1, 1);
                } else {
                    clonedColumns.splice(columnFrom, 1);
                }
            } else {
                clonedColumns.splice(columnTo + 1, 0, colFrom);
                if (columnTo < columnFrom) {
                    clonedColumns.splice(columnFrom + 1, 1);
                } else {
                    clonedColumns.splice(columnFrom, 1);
                }
            }
            clonedColumns.forEach((c, index) => c.order = index);

            updateBuildingColumns(part, clonedColumns.map(c => ({ ...c })));
        }
    }

    static handleColumnTypeChange<ColumnType>(
        type: ColumnType, part: Part<any, any>, column: Column<any, any, any, any>, repeatingFloorTypes, updateBuildingColumn, setUpDefaultColumnFromDefinition
    ) {
        const c = setUpDefaultColumnFromDefinition(type, part.type);
        c.id = column.id;

        const repeatingFloorSegments = c.segments.filter(s => repeatingFloorTypes.includes(s.type));
        const originalMidSecs = column.segments.filter(s => repeatingFloorTypes.includes(s.type));

        repeatingFloorSegments.forEach((m, index) => {
            m.floorsInSegment = originalMidSecs[index].floorsInSegment;
        })

        updateBuildingColumn(part, c);
    }


    static handleSegmentSubtypeChange<SegmentType>(e, part: Part<any, any>, column: Column<any, any, any, any>, segment: SegmentType, columnDefs, updateBuildingColumn) {
        const subtype = e.target.value;
        const seg = column.segments.find(seg => seg.type === segment);
        seg.subtype = subtype;

        const segmentDef = RefineService.getSegmentDefinitionFromTypeAndSubtype(column.type, segment, subtype, columnDefs);
        seg.floorsInSegment = segmentDef.floorVariants[0].floors;
        seg.variant = segmentDef.floorVariants[0].variants[0].variant;
        seg.sectionTypes = segmentDef.floorVariants[0].variants[0].sections;
        if (part.type === 'headRight' && segmentDef.floorVariants[0].variants.length === 2) {
            seg.variant = segmentDef.floorVariants[0].variants[1].variant;
            seg.sectionTypes = segmentDef.floorVariants[0].variants[1].sections;
        }
        updateBuildingColumn(part, { ...column, segments: [...column.segments.map(s => ({ ...s }))] });
    }

}